import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CircleLoader, ErrorScreen, TranslatedTextError } from 'src/components';
import { Loading } from 'src/components/Loading';
import * as Yup from 'yup';
import { useGetClientDeliveryRequestForRating } from './hooks/useGetClientDeliveryRequestForRating';
import { useSubmitClientDeliveryRating } from './hooks/useSubmitClientDeliveryRating';

type Props = {
  token: string;
};

enum CustomerExperienceFeedbackRating {
  VERY_BAD = 'VERY_BAD',
  BAD = 'BAD',
  NEUTRAL = 'NEUTRAL',
  GOOD = 'GOOD',
  VERY_GOOD = 'VERY_GOOD',
}

const ratingOptions = [
  { value: CustomerExperienceFeedbackRating.VERY_BAD, label: 'Very Bad' },
  { value: CustomerExperienceFeedbackRating.BAD, label: 'Bad' },
  { value: CustomerExperienceFeedbackRating.NEUTRAL, label: 'Neutral' },
  { value: CustomerExperienceFeedbackRating.GOOD, label: 'Good' },
  { value: CustomerExperienceFeedbackRating.VERY_GOOD, label: 'Very Good' },
];

type FeedbackFormData = {
  driverRating: CustomerExperienceFeedbackRating;
  serviceRating: CustomerExperienceFeedbackRating;
  note: string;
};

const validationSchema = Yup.object().shape({
  driverRating: Yup.string().required('Merci de noter le livreur'),
  serviceRating: Yup.string().required('Merci de noter le service'),
  note: Yup.string().max(500, 'Remarque ne peut pas dépasser 500 caractères'),
});

export const PublicClientDeliveryRequestClientRating: FC<Props> = ({ token }) => {
  const {
    isLoading: isSubmitting,
    error: submitError,
    submitClientDeliveryRating,
    data: submitData,
  } = useSubmitClientDeliveryRating();

  const { isLoading, error, fetchData, data } = useGetClientDeliveryRequestForRating();
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FeedbackFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      driverRating: '' as CustomerExperienceFeedbackRating,
      serviceRating: '' as CustomerExperienceFeedbackRating,
      note: '',
    },
  });

  useEffect(() => {
    fetchData(token);
  }, [fetchData, token]);

  const retryReload = useCallback(() => {
    fetchData(token);
  }, [fetchData, token]);

  const onSubmit = (formData: FeedbackFormData) => {
    submitClientDeliveryRating({
      token,
      customerExperienceFeedbackNote: formData.note,
      customerExperienceFeedbackRatingDriver: formData.driverRating,
      customerExperienceFeedbackRatingService: formData.serviceRating,
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error || !data) {
    return <ErrorScreen error={error ?? new Error('Aucune demande trouvée')} retry={retryReload} />;
  }

  if (submitData) {
    return (
      <div className="flex flex-col items-center min-h-screen bg-gray-50 py-10 px-4">
        <div className="w-full max-w-xl bg-white rounded-lg shadow-md">
          <div className="bg-blue-600 text-white py-4 px-6 rounded-t-lg">
            <h1 className="text-lg font-semibold">{t('client-delivery-requests.page-title')}</h1>
          </div>
          <div className="p-6">
            <p
              className="mb-4 text-gray-700"
              dangerouslySetInnerHTML={{ __html: t('client-delivery-requests.hello', { name: data.name }) }}
            />
            <p className="text-green-500 mb-6">Smartcolis et votre agence vous remercient pour votre avis !</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-50 py-10 px-4">
      <div className="w-full max-w-xl bg-white rounded-lg shadow-md">
        <div className="bg-blue-600 text-white py-4 px-6 rounded-t-lg">
          <h1 className="text-lg font-semibold">{t('client-delivery-requests.page-title')}</h1>
        </div>
        <div className="p-6">
          <p
            className="mb-4 text-gray-700"
            dangerouslySetInnerHTML={{ __html: t('client-delivery-requests.hello', { name: data.name }) }}
          />
          <p className="text-gray-700 mb-6">Vous avez été invité à évaluer votre expérience de livraison à domicile.</p>

          {/* Delivery Information */}
          <div className="bg-gray-100 rounded-md p-4 border-l-4 border-blue-500 mb-6">
            <p className="text-sm text-gray-600">
              <strong>Envoi Numéro :</strong> {data.shipping.tracker}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Numéro de la demande :</strong> {data.tracker}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Colis livré à l'agence :</strong> {data.shipping?.toAgency?.name}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Adresse de livraison :</strong> {data.address}
            </p>
          </div>

          {/* Rating Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="mb-6">
              <h3 className="text-gray-700 font-semibold">{t('client-delivery-requests.driver-rating')}</h3>
              <Controller
                name="driverRating"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className={`w-full p-2 border rounded-md ${errors.driverRating ? 'border-red-500' : ''}`}
                  >
                    <option value="">{t('client-delivery-requests.select-rating')}</option>
                    {ratingOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(`client-delivery-requests.${option.value.toLowerCase()}`, option.label)}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.driverRating && <p className="text-red-500 text-sm">{errors.driverRating.message}</p>}
            </div>

            <div className="mb-6">
              <h3 className="text-gray-700 font-semibold">{t('client-delivery-requests.service-rating')}</h3>
              <Controller
                name="serviceRating"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className={`w-full p-2 border rounded-md ${errors.serviceRating ? 'border-red-500' : ''}`}
                  >
                    <option value="">{t('client-delivery-requests.select-rating')}</option>
                    {ratingOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(`client-delivery-requests.${option.value.toLowerCase()}`, option.label)}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.serviceRating && <p className="text-red-500 text-sm">{errors.serviceRating.message}</p>}
            </div>

            <div className="mb-6">
              <h3 className="text-gray-700 font-semibold">{t('client-delivery-requests.notes')}</h3>
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    className={`w-full p-2 border rounded-md ${errors.note ? 'border-red-500' : ''}`}
                    placeholder={t('client-delivery-requests.notes-placeholder')}
                  />
                )}
              />
              {errors.note && <p className="text-red-500 text-sm">{errors.note.message}</p>}
            </div>
            {!!submitError && <TranslatedTextError message={submitError.message} />}
            {isSubmitting ? (
              <CircleLoader />
            ) : (
              <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-md">
                {t('client-delivery-requests.submit')}
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
